<template>
<div class="chapter">
<div class="inner_page">
<h2 class="ph7"><a class="calibre1" id="Chapter_One"></a>Chapter One</h2>
<p class="center">THE INFANT</p>
<p class="ph7">Chapter One: The Infant</p>
<p class="calibre3">On a June morning in the year 1859 Sir Richard Flower of Barton
Flowers in the county of Southampton decided that the weather was
propitious for his annual progress on horseback round the confines
of his demesne. The order was given to saddle his gray gelding; Lady
Flower was informed that her husband would dine two hours later than
usual, and upon her expressing alarm at the prospect of so long a fast
for him, she was reassured by a farther announcement that he would
fortify himself against the strain of waiting until six o'clock for
his dinner with light refreshment at one of the outlying farms. Lady
Flower sent back word to say how much she regretted not having known of
Sir Richard's expedition earlier in order that she might have made an
effort to overcome her headache and bid him farewell in person. To this
the baronet replied with a solemn admonition to her ladyship's maid
that her ladyship must on no account do anything to make her headache
worse. The exchange<span id="pgepubid00001" title="[Pg 4]"><a id="Page_4" title="[Pg 4]"></a></span> of courtesies being thus complete, Sir Richard
mounted his gray gelding and set out, pausing for a moment at the top
of the drive to look back at the Hall and respond with his crop to a
handkerchief that fluttered from an upper window. In the manner of
shaking his crop Sir Richard succeeded in conveying a reproof for the
indiscretion of rising from bed, affection for his beloved wife, and
gratification at the devotion displayed for himself. Then he turned
his horse's head to the left and cantered down a grassy avenue between
ancient oak trees.</p>
<p class="calibre3">Sir Richard was accustomed to give much thought to his position
as holder of one of the oldest baronetcies in England, to the
responsibilities that such a position laid upon himself, to the beauty
and fertility of his demesne, to the timbered glories of his Hall, and
to the honorable record of his family; but on the day annually devoted
to riding round his ten thousand acres he never allowed himself to
think about anything else. He even went so far, when in the depths of
the wood neither squirrel moved nor bird chattered and there was none
but the gray gelding to overhear him, as to cry aloud in exultation
the motto of his house <i class="calibre8">Floreant Flores</i>. On this day dedicated to
himself, his family, and his land, Sir Richard indulged in so many
whimsicalities of behavior that an observer might have supposed him
the prey of madness or the victim of degraded superstition. Thus at
one point he dismounted from his horse and, kneeling in the middle
of the ride, placed<span id="pgepubid00002" title="[Pg 5]"><a id="Page_5" title="[Pg 5]"></a></span> an outspread palm upon the cushions of moss
and incorporated the thousands of green and golden stars within his
allegiance. He went farther; he laid bare the earth beneath and
commanded a congregation of disturbed millipedes to acknowledge him as
master. He made with his hands a cup to contain the black earth, and
let it trickle through his fingers as a miser might play with his gold.
"Mine," he said aloud, and stood for a moment in amazement at one who
owned not merely all the green world within sight, but four thousand
miles of unimaginable territory beneath his feet. "Mine," he repeated,
"and after me John, and after John another Richard. Praise God that
I appreciate the state of life to which He has called me;" with this
apostrophe the baronet swept off his high silk hat to salute his patron.</p>
<p class="calibre3">Sir Richard kept such extravagance of speech and gesture for the
solitude of the woodland. No sooner had he emerged into one of the
deep, hazel-bordered lanes that intersecting his demesne reminded him,
deserted though they were, of the world beyond his boundaries, than
he became the least fantastic inhabitant of that decorous countryside
of well-tilled farms and preserved coverts. Sir Richard was close on
sixty; but his slim figure, upright carriage, and clear-cut features
enhanced by iron-gray whiskers, bushy enough to show that he was not
afraid of the fashion and yet not so full as to mark him down the slave
of that fashion, made him appear younger at<span id="pgepubid00003" title="[Pg 6]"><a id="Page_6" title="[Pg 6]"></a></span> a period when twenty-five
looked middle-aged. Every good horseman gives the impression of being
part of his steed, and Sir Richard on his gray gelding, with his gray
whiskers and gray riding breeches and gray frieze tail-coat was as
natural a centaur as Chiron himself.</p>
<p class="calibre3">"Good morning, Sir Richard."</p>
<p class="calibre3">The baronet pulled up to exchange a word with the first of his tenant
farmers he was to meet that day, a bull-necked, stubby man who was
leaning over a gate against a background of bright green barley.</p>
<p class="calibre3">"Good morning to you, Wilberforce. Your barley's looking uncommonly
well."</p>
<p class="calibre3">"Beautiful, Sir Richard, beautiful. Some grumbles, but not me, Sir
Richard, not me. May was bad for fruit with all that hail we had. But
the crops didn't suffer. Will you be passing by the farm, Sir Richard?"</p>
<p class="calibre3">"Not this morning, Wilberforce. I'm taking my annual ride round the
estate. You know my old custom."</p>
<p class="calibre3">"None better, Sir Richard. And what a one you be for keeping up old
customs, if you'll permit the liberty of the observation, Sir Richard.
And glad I am for one to have such a landlord in these days when Jack
thinks himself so good as his master. And how's Mr. John, Sir Richard?"</p>
<p class="calibre3">"Mr. John is well, very well. He hopes to be quartered at Aldershot
presently, when we may expect to see something of him."</p>
<p class="calibre3"><span id="pgepubid00004" title="[Pg 7]"><a id="Page_7" title="[Pg 7]"></a></span></p>
<p class="calibre3">"It'll be a grand day for Barton Flowers when the village turns out to
see the conquering hero come. Mr. John must have been proud when Her
Majesty pinned on the Victoria Cross with her own hands at Buckingham
Palace the other day. But, as I said to all of 'em, Her Majesty must
have been proud of Mr. John when she were a-pinning of it on."</p>
<p class="calibre3">"Yes, I believe he deserved his honor," said the father, trying to
look unconcerned. "Of course you saw the little account of it in the
newspaper?"</p>
<p class="calibre3">Farmer Wilberforce gave his landlord the pleasure of supposing that he
had not yet read the account, whereupon Sir Richard took a cutting from
his waistcoat pocket and read aloud as follows:</p>
<blockquote class="calibre14"><div class="calibre2">
<p class="calibre3">Lieutenant (now Captain) John Flower, Royal Artillery.</p>
<p class="calibre3">Date of act of bravery, 5th November, 1854.</p>
<p class="calibre3">For having at the Battle of Inkerman personally attacked three
Russians, and, with the gunners of his Division of the battery,
prevented the Russians from doing mischief to the guns which they had
surrounded.</p>
<p class="calibre3">Part of a regiment of English infantry had previously retired through
the battery in front of this body of Russians.</p>
</div>
</blockquote>
<p class="calibre3">"He had to wait a long time for his deed to be recognized," said
the father, replacing the slip of paper in his pocket with a sigh
of satisfaction.<span id="pgepubid00005" title="[Pg 8]"><a id="Page_8" title="[Pg 8]"></a></span> "Good morning to you, Wilberforce. I mustn't stay
gossiping here any longer. I've a good many miles in front of me, you
know."</p>
<p class="calibre3">Sir Richard rode on, his mind full of his elder son's valor. He should
be thinking about marriage, though. It was time to see a grandson at
the Hall. One was apt to forget how fast the years were going by. How
old was John now? Thirty. So he was, by gad, thirty. Yes, he must be
getting married. Not much difficulty about that, the proud father
laughed to himself. Handsome, brave, the heir to Barton Flowers! It
was right that he should take his profession seriously, but after
the Crimea and the Mutiny he could claim to have served his country
well, could afford to sell out and prepare himself to administer the
property he would one day inherit. One day ... but not just yet. "No,
not just yet," Sir Richard murmured, gripping the flanks of the gray
horse tightly in pride of his own strength. And perhaps at this moment
when the electric telegram was almost daily bringing news of French
victories in Italy, and when that rascal Napoleon might be forming who
knows what schemes to invade England, yes, perhaps at this moment,
Captain John Flower should stick to his guns. Still, he would talk to
his wife about the boy's marriage. He hoped that when he arrived home
again he should find that headache sufficiently improved to let her
discuss the subject with keenness and intelligence. The right plan
was to invite some eligible young women to visit<span id="pgepubid00006" title="[Pg 9]"><a id="Page_9" title="[Pg 9]"></a></span> the Hall during
John's next furlough, and if luck should station him at Aldershot to
take care that whenever he drove over to Barton he should find an
attraction at home. Luckily there were plenty of eligible young women
in the neighborhood. Sir Richard was enumerating the possible wives
for his heir when the disquieting thought occurred to him that John,
like his father before him, might look beyond Hampshire for a wife.
Not that for a single moment he had regretted his own choice; but
what might be done once with success might end in disaster if fortune
were tempted again. Anybody who had been made aware of Sir Richard's
thoughts at this moment might have been pardoned for supposing that
he had found a wife of beauty, merit, and ability in a lower stratum
of society. As a matter of fact, the present Lady Flower was the
daughter of one of Wellington's most gallant officers and a French
lady of rank whose father had taken refuge from the Terror in England,
where he had preferred to remain during the Napoleonic tyranny. It
was the French blood that made Sir Richard feel he was committing a
breach of tradition in marrying Miss Helen Baxter. To have introduced
French blood into the Flowers, notwithstanding the pride of the family
in their Norman origin, still seemed to him an astonishing piece of
audacity; and even now he could shudder to think what his father would
have said, had his father been alive when he married. Yet his wedded
life had been one of un<span title="[Pg 10]"><a id="Page_10" title="[Pg 10]"></a></span>broken happiness, and Helen had not betrayed
the least sign of her mixed origin unless perhaps in an incurable
propensity to succumb to violent headaches, which she dignified, or as
her husband preferred to think, Frenchified by calling migraines. The
old family doctor attributed them to nerves, and nerves, Sir Richard
felt, were French, not English, so that if Doctor Wilkinson was right,
the headaches must have been inherited from her French mother. There
was nothing of the Frenchman in the elder son John. He never had a
headache in his life, and he had won the Victoria Cross. English to the
backbone was John. But Edward...?</p>
<p class="calibre3">Sir Richard, who had been trotting gaily along his boundaries, pulled
up his horse to a walk, because the personality and character of
his younger son perplexed him. Edward had headaches, was prone to
day-dreaming, and at twenty-eight showed no sign of making any progress
at the Bar, to which without apparently the slightest taste for a legal
career he had recently been called. Headaches, day-dreams, instability,
these were not English qualities. What had Edward been doing down at
home all the summer? How could he expect to be a successful barrister
if he left his chambers in Pump Court to take care of themselves? If
John had been a barrister, he would have made his mark by now. Yet
Edward had been endowed with more brains than John. John was diligent,
determined; but Edward had the brains. It had been the ancient custom
of the<span id="pgepubid00007" title="[Pg 11]"><a id="Page_11" title="[Pg 11]"></a></span> Flowers to send the eldest son to Winchester, the others to
Eton. Sir Richard, who was a Wykhamist, had broken the tradition
by sending John to Eton and Edward to Winchester, partly because
he thought that Winchester would eradicate more sternly any French
symptoms that appeared in Edward, partly because he believed that what
was known as cleverness in a boy would receive more encouragement at
the older foundation. But Edward had been a disappointment. His career
at Winchester had been undistinguished, and he had gone down from New
College without taking a degree. That was the moment when his father
should have been firm with him, when he should have insisted upon
his making his own way in the world without parental assistance. But
Helen had intervened, and she intervened so rarely that when she did
her husband was always defeated. Edward had expressed a half-hearted
desire to read for the Bar, and he had allowed himself to be persuaded
into making the necessary allowance. What was the result? Edward at
twenty-eight as little able to provide for himself as he was at eight!
It had been all very well for his mother to plead for his company over
long months at Barton to console her for the absence of her elder son
first in the Crimea and then in India. But John had been back a year
now, and Edward spent more time than ever at home. Confound it, the
problem of Edward's future was<span title="[Pg 12]"><a id="Page_12" title="[Pg 12]"></a></span> spoiling the day, and in a burst of
irritation the baronet spurred his horse to a canter.</p>
<p class="calibre3">At this point the boundary of Sir Richard's estate might have been
the subject of litigation had there been enough people interested
to litigate. It was the old dispute over common land which had been
gradually enclosed by the lord of the manor. In this case the issue
was complicated by the fact that the head of the Flowers was as such
himself a commoner, and it was difficult to prove that a commoner had
no right to plant beechwoods if he was so minded. This had been the
Flower method of encroachment. At this date there were only three other
families of commoners left, and inasmuch as these gained a miserable
livelihood by poaching Sir Richard's coverts rather than by pasturing
a few scrawny geese, there was no doubt that before long the landlord
would succeed in fixing his boundary on the far side of the common. At
present the common extended for a mile, a narrow strip of coarse grass
land two hundred yards wide at its greatest breadth along the baronet's
dark beechwoods. Beyond the common the railway cut its track through
the meadows of another landowner, and Sir Richard laughed to think how
twenty years ago he had refused to let the line run through his land.</p>
<p class="calibre3">"That's the way good estates are ruined," he thought complacently,
urging his horse from a canter to a gallop.</p>
<p class="calibre3"><span id="pgepubid00008" title="[Pg 13]"><a id="Page_13" title="[Pg 13]"></a></span></p>
<p class="calibre3">The wild commoners came out from their hovels to stare at him as he
flew past, and congratulated themselves that he had not noticed how
much turf in excess of their allowance had recently been cut.</p>
<p class="calibre3">At the end of the gallop Sir Richard reined in his horse to a walk
that he might move slowly and admiringly through a plantation of
larches he had put in ten years ago, which now in its symmetry and
silence impressed him as a painter might be impressed by the beauty
of an early work he had forgotten. Sir Richard regretted that he had
not made a similar plantation near the Hall, so that his wife might
enjoy walking upon this pale grass where the sun shone with so dim
and so diffused a light. He was convinced that the experience would
appeal to that romantic side of her character which expressed itself in
migraines. Yes, it was a pity he had not thought of planting another
within access of the Hall. He was now in the most remote corner of his
demesne, and it would be difficult to drive her to this place without
considerable discomfort. This plantation must be making a fine screen
for old Taylor's orchard by now, thought Sir Richard. The old man had
grumbled when first his landlord had insisted upon afforesting that
useless field, covered with thistles and ragwort; he would admit now
that his landlord had been right. But the old man was always grumbling.
No doubt if he met him to-day he would be full of woe over the thunder
and hail of last month, vowing that none of his blossom<span id="pgepubid00009" title="[Pg 14]"><a id="Page_14" title="[Pg 14]"></a></span> had set and
that the season would be a dead loss in consequence. How different
from Wilberforce, who had recognized most sensibly the promise of the
arable crops! The fact of it was, old Taylor was growing too old for
the responsibility of a large farm. Of course he had not the slightest
intention of turning him out, but he did wish that old Taylor showed
more signs of appreciating his landlord's consideration. That was the
trouble with people, Sir Richard sighed to himself, one did all that
was possible for them and received nothing in return. If only some of
the tenants who grumbled at the least delay in carrying out necessary
repairs would try to understand the point of view of the landlord.
Nowadays people only tried to understand their own point of view. Yes,
the age was degenerating, humanity was not what it was.</p>
<p class="calibre3">The prey of these pessimistic reflections, Sir Richard had allowed the
horse to take his own pace; the progress had been slow and silent; and
when the long central aisle of the plantation made an abrupt curve
at its conclusion Sir Richard found himself in old Taylor's orchard
so suddenly that he had to dismount in a hurry to save his silk hat
from being knocked off by the boughs of the apple trees. As his foot
touched the ground, he saw in a sun-flecked space about eighty yards
from where he was standing two figures disengage from a close embrace.
Sir Richard recognized from the color of her auburn hair old Taylor's
granddaughter, Elizabeth, and<span id="pgepubid00010" title="[Pg 15]"><a id="Page_15" title="[Pg 15]"></a></span> he was on the verge of a smile for
youth and love in the summer time when he perceived that the man was
his own son, Edward. He raised his riding-crop with a gesture of rage,
while the lovers as if even a moment's separation were bitter as death
clung together in a fresh embrace, standing heedless of all except
their love, heedless of the young apples that fell from time to time
from every tree, heedless of the noise Sir Richard's horse made in
cropping the tender grass, heedless of Sir Richard's foot stamped upon
the ground in anger, nor even looking round when he jerked his horse's
bridle, remounted, and galloped back the way he had come down the long
central aisle between the larches.</p>
<p class="calibre3">"The damned philandering puppy," he muttered to himself, as he came out
from the plantation and set the gray to gallop more swiftly than before
over the common land. He paid no attention to the wild commoners, who
seeing the baronet return at this furious pace supposed that he had
been made aware of their depredations upon the turf and ran to hide
from his wrath in the dark bordering beeches. He paid no attention to
the geese that flapped across his path except to give the gelding a
cruel jab when he swerved in his stride. It was barely two o'clock when
Sir Richard reached the Hall, having for the first time in thirty-five
years failed at his yearly task of riding round the confines of his ten
thousand acres. So deeply enraged was he with his son's conduct that he
neither sent<span id="pgepubid00011" title="[Pg 16]"><a id="Page_16" title="[Pg 16]"></a></span> up to warn his wife of his early return nor even inquired
after her headache. He shut himself in his big library, pacing up and
down among the rows of books, the titles of which wrote themselves upon
his mind more rapidly but perhaps not less intelligibly than they had
written themselves on the minds of generations of Flowers. Sir Richard
glared at the busts of poets, orators, and philosophers posed with such
unconcern, with such coolness and such contempt above the cornice of
the shelves. If Homer, Demosthenes and Plato had not been out of reach,
the baronet would have swept them from their perch to the ground.
Instead he pulled the bell rope violently.</p>
<p class="calibre3">"When Mr. Edward comes in," he told the butler, "I wish to see him at
once."</p>
<p class="calibre3">"Very good, Sir Richard," said the butler apprehensively, and as the
old man went out of the library Sir Richard wondered if his son's
conduct was already a topic in the steward's room and servants' hall.
In the middle of his rage there was a tap at the door, and his wife
entered to a gruff summons. Lady Flower was a small, dainty woman whose
smallness and daintiness was accentuated by the vast crinolines of
the moment. Although she was almost fifty, her black hair lacked the
faintest film of gray, her ivory skin showed few lines. To Sir Richard
she seemed the same as when thirty-one years ago he had married her.
She never came into a room but his mind went back to the first sight<span id="pgepubid00012" title="[Pg 17]"><a id="Page_17" title="[Pg 17]"></a></span>
of her dressed in a short flounced skirt with her black hair tied high
with roses and ribands; and it seemed not she but her clothes which had
grown older and more stately with years.</p>
<p class="calibre3">"My dear, what is the matter?" she asked. "What has upset you?"</p>
<p class="calibre3">The distressed father poured out his tale.</p>
<p class="calibre3">"But aren't you taking it all too seriously?" his wife suggested.
"Edward has only found a Graziella at Barton. <i class="calibre8">Il y a toujours des
petits amoureux....</i>"</p>
<p class="calibre3">"For God's sake don't talk French!" Sir Richard burst in. "There's
nothing like French for giving an unpleasant turn to the conversation."</p>
<p class="calibre3">"It was tactless of me," she apologized, seating herself in a
high-backed chair where she looked as tranquil and as much assured
as one of the classic busts eyeing infinity above the books. "But
seriously the Taylor girl is a pretty little thing, and if Edward is
not imprudent there is most surely no harm in a few kisses."</p>
<p class="calibre3">"Helen, your remarks border on cynicism," said Sir Richard. "I know
that you have always maintained your right to discuss matters which in
England I think we have reason in not encouraging women to discuss;
but really when your advanced views are applied to your own children
I think it is time for me to protest. After all, if you had a French
mother, my dear, you are quite definitely and unmistakably English
yourself. But please do not<span id="pgepubid00013" title="[Pg 18]"><a id="Page_18" title="[Pg 18]"></a></span> let us cover up Edward's behavior with
side issues. You know how much I have deplored his laziness, how much I
have objected to his spending most of his time here, and how necessary
it is for him as a younger son to supplement with a profession any
allowance I am able to give him in the future from my own savings. I
repeat, you know all this, and yet when I discover that the reason for
his continually living with his parents is not the pleasure of their
society, but a low passion for the granddaughter of one of his father's
tenants, it becomes obvious that Edward's behavior can no longer be
tolerated. Of course he has headaches if he behaves like this," Sir
Richard went on indignantly. "Of course he finds the air of Pump
Court too stuffy in June. You must remember, my dear, that Edward is
twenty-eight. We are not discussing the calf love of a schoolboy."</p>
<p class="calibre3">"Well, all I beg is that you will handle him tactfully," said Lady
Flower. "Now, if I could only persuade you to let me talk to him...."</p>
<p class="calibre3">"Certainly not. On such a subject most certainly not," Sir Richard
shouted.</p>
<p class="calibre3">"But if you jump down his throat and treat him like a schoolboy, he may
do something really serious." She paused to sniff a silver vinaigrette,
while the suggestion buried itself like an arrow in the heavy ground of
her husband's mind.</p>
<p class="calibre3">"Really serious?" he echoed in a moment's perplexity. "Good God! you
are not suggesting that<span id="pgepubid00014" title="[Pg 19]"><a id="Page_19" title="[Pg 19]"></a></span> he might want to marry her? That would indeed
be the end of everything."</p>
<p class="calibre3">"That is precisely what I am trying to tell you," said his wife. "That
is why I am trying to hint that you should not take too high a moral
tone."</p>
<p class="calibre3">"Good heavens, my dear, what outrageous remarks you do make. And yet on
this occasion I really believe you are justified in making them."</p>
<p class="calibre3">The baronet sank down into a chair opposite his wife and allowed her to
lean over and pat his cheek as if he were a disconsolate boy.</p>
<p class="calibre3">"Don't you think it would be wiser for me to carry through this scene?"
she pressed.</p>
<p class="calibre3">He waved the suggestion aside. "No, no, my dear. I appreciate your
desire to spare me pain, but what I have to say to Edward must be said
as from a man to a man. Hark! I hear his horse coming up the drive.
Leave us together, my dear, leave us, I beg you...."</p>
<p class="calibre3">Lady Flower hesitated for one moment longer, but perceiving that her
husband was not to be moved from his resolve, acquitted herself of
all responsibility with a gesture of her white hands, and without a
backward glance of entreaty floated from the room.</p>
<p class="calibre3">Edward Flower resembled his mother in features and complexion, but
in figure he was tall and slim like his father. He seemed to divine
that the interview to which he had been summoned was likely to be
disagreeable, for he waited by the door of the<span id="pgepubid00015" title="[Pg 20]"><a id="Page_20" title="[Pg 20]"></a></span> library when he had
closed it behind him as if he hoped that he had made a mistake in thus
intruding.</p>
<p class="calibre3">"Bates told me you wished to speak to me, sir."</p>
<p class="calibre3">"I did. I do. Don't let us beat about the bush. And come into the room!
I can't shout what I have to say."</p>
<p class="calibre3">However discreetly hushed the baronet's voice was going to be when he
attacked his son upon the situation in Taylor's orchard, it was loud
enough at present.</p>
<p class="calibre3">"I am at your service, sir," said Edward quietly, taking the chair in
which a few minutes ago his mother had been sitting.</p>
<p class="calibre3">"I started out this morning to ride round the estate," Sir Richard
began. "On my way I passed by Taylor's orchard." He paused with a stern
glance at his son. "Well, sir?" he demanded.</p>
<p class="calibre3">"And I'm glad you did, papa," said Edward eagerly. The character of
this interview drove him back unconsciously to childhood's manner of
address.</p>
<p class="calibre3">"You're glad I did?" the baronet echoed. "By gad, sir, you're a cooler
hand at this game than I gave you credit for. I'm thankful I did not
allow your mother to speak to you on this subject."</p>
<p class="calibre3">"Did my mother wish to speak to me?" Edward broke in. "Ah, she would
understand, and I fear that you, sir, may be prejudiced by the humble
station of the dear girl I am going to marry."</p>
<p class="calibre3">"Marry!" the baronet shouted. "This is not a<span id="pgepubid00016" title="[Pg 21]"><a id="Page_21" title="[Pg 21]"></a></span> moment for levity, sir. I
sent for you to say that I won't have you philandering with the females
on my estate. You know I disapprove of the manner you idle away your
time here when you should be working at your profession. But if you do
stay here, by God you shall stay here like a gentleman and a Flower,
with respect for the domestic happiness of your father's tenants. We've
never yet had a scandal of that kind in our family, and if my son
brings such a scandal about I'll disown him."</p>
<p class="calibre3">"I have already told you, sir, that the young woman will shortly become
my wife. There is no question of scandal. I love her passionately,
devotedly. She gives me all and more in return. She is a modest and
beautiful girl. I am old enough to know my own mind. I am sorry to seem
disrespectful, sir, but nothing that you can say will alter my resolve."</p>
<p class="calibre3">"I'll disinherit you."</p>
<p class="calibre3">"I must put up with that."</p>
<p class="calibre3">"I'll disown you. You shall never cross the threshold of this house
again."</p>
<p class="calibre3">"I must put up even with that," said Edward sadly.</p>
<p class="calibre3">"Thank God I have another son who would never disgrace his father and
his father's name thus."</p>
<p class="calibre3">"I know that I have been a disappointment to you, sir; but this is
not the moment to make excuses for my carelessness in the past nor to
try your patience with promises of reform in the future. I firmly<span id="pgepubid00017" title="[Pg 22]"><a id="Page_22" title="[Pg 22]"></a></span>
believe that marriage with Elizabeth Taylor will give me that very
stability and perseverance in which I have hitherto shown myself so
lacking. If you had evinced less anger at my decision, I should have
enlarged upon this benefit to my character; but in your present mood I
am conscious that anything I say will only serve to enrage you against
me more than ever. Luckily I am not your heir, and my brother, as you
justly observe, will know better than I how to uphold the honor of
your house—since you have disowned me, I hesitate to say <i class="calibre8">our</i> house.
Believe me, my dear, dear father, when I say that only the assurance
of my whole life's happiness depending upon my marriage with Elizabeth
keeps me from obeying your wishes. There is nothing to add except my
deep regret for the secrecy I have maintained throughout. I can assure
you that in acting in what may seem to you an underhand manner I was
endeavoring to spare you pain, so that when the secret had to come out,
which would have been to-morrow, for it is to-morrow that we are to
be married, you would have been spared the annoyance of contesting a
situation which was a <i class="calibre8">fait accompli</i>."</p>
<p class="calibre3">"Damn it, don't talk French, and get out of my sight," Sir Richard
shouted, louder than he had shouted yet, for his son's long speech had
given his rage time to seethe in his breast, and it now burst forth
with double volume.</p>
<p class="calibre3">Edward bowed his head and rising from his chair<span id="pgepubid00018" title="[Pg 23]"><a id="Page_23" title="[Pg 23]"></a></span> went gloomily from the
room. He found his mother standing in the corridor outside, and at a
signal he followed her upstairs to her boudoir.</p>
<p class="calibre3">Edward contrasted his mother's calm with his father's fury, and yet
when she sat upright on a wide stool, composing her full skirts of
amber sarsanet with hands that seemed incredibly small against the
vast pendulous sleeves from which they emerged, Edward was more uneasy
in presence of that calm than when he was being buffeted by his
father's storms. There was an ivory polish, an ivory hardness, an ivory
resilience about his mother that made his heart beat with a dread of
this delicate creature who within his earliest memories had always come
to the help of his ineffectiveness, but who now sat regarding him from
eyes that seemed as hard as agates.</p>
<p class="calibre3">"Listen, Edward," she said quickly. "I overheard what your father
said, and I understand that you have announced your resolve to marry
this ..." Lady Flower paused for a second as if she were pondering the
effect upon her son of describing the young woman too brutally ...
"this pretty country girl," she continued, sure now of the key in which
her persuasion should be played, a key of light irony, of compassionate
ridicule which must bring the sensitive Edward to a perception of the
impossibility of what he was proposing. "I think I have seen her once
or twice hanging out the clothes or feeding her grandfather's chickens.
She has red<span id="pgepubid00019" title="[Pg 24]"><a id="Page_24" title="[Pg 24]"></a></span> hair, has she not? And is she not much freckled?"</p>
<p class="calibre3">"She has glorious hair," Edward avowed. "And her complexion is perfect."</p>
<p class="calibre3">"Red-headed women usually freckle somewhat easily," said his mother
indifferently. "But let that pass, we will admit her beauty. Personally
I distrust red-haired women. There is something of the fox...."</p>
<p class="calibre3">Lady Flower broke off to shrug her shoulders in distaste.</p>
<p class="calibre3">"I should hardly describe her hair as red myself," Edward said. "It has
reddish tints, but...."</p>
<p class="calibre3">"My dear boy, you are not proposing to paint this young woman; you are
proposing to marry her. When your father came home furious because he
had seen you kissing her on a garden-seat or some such romantic spot,
I took your part. Indeed, your father was shocked at my inability to
see much harm in kissing a pretty village maiden. But marriage, ah,
<i class="calibre8">par example, mais ça c'est un peu fort, tu sais</i>. Have you really
considered what it will mean in a few years' time when your Graziella
coarsens? You will have to earn your own living, for I know your father
well enough to be sure that if you do marry this girl he will keep his
word and cut you off. That means that you will not have the leisure
to educate her, that you will be dragged down to her level, that you
will...."</p>
<p class="calibre3">"Please, mamma, please I beg you not to say any more. My mind is made
up, and if I have to re<span id="pgepubid00020" title="[Pg 25]"><a id="Page_25" title="[Pg 25]"></a></span>nounce my family I want to leave you without
the least bitterness. I will not hear a word against Elizabeth. I adore
her."</p>
<p class="calibre3">"And where will you live?" his mother inquired, biting her lips.</p>
<p class="calibre3">"I am going to ask her grandfather if he will take me on at his farm."</p>
<p class="calibre3">"You are going to live within a few miles of us as a farm hand?"</p>
<p class="calibre3">"I shall be happy," said Edward, miserably aware of his mother's
contempt.</p>
<p class="calibre3">"I have always defended you until now. But such ... such.... Oh, I have
no word for such a despicable suggestion. I have finished with you,
Edward. I almost wish I could shout as loudly as your father to tell
you how completely I despise you. Go to that minx, who in a year will
despise you as much as I do, and who will play you false with the first
handsome plowboy she meets. And you'll deserve it, you weakling!"</p>
<p class="calibre3">Edward rushed from his mother's room, and when he had packed his
possessions went to Bates, the old butler, and asked him for the
servants' cart to take his luggage to Long Orchard Farm. His mother's
last speech had made much easier the task of cutting himself off from
his family, and when he set out down the drive he had not one regret
for what he was losing. Edward depended much on other people, and now
that one of those on whom he had most securely depended had let him
fall, he clung<span id="pgepubid00021" title="[Pg 26]"><a id="Page_26" title="[Pg 26]"></a></span> more closely to the other. Elizabeth had long lamented
the worry she was likely to prove to him when his family was informed
of the marriage, and he was glad now to be able to meet her before the
day with the news that he owned no family except hers. How surprised
she would be to see him again so soon, for they had just lived through
that passionate farewell until they should meet to-morrow morning at
the door of the church. A misgiving came over Edward. What if Elizabeth
should be so much distressed by the news of the breach that she should
not keep her word? Was it wise in any case to upset her on the eve
of the wedding? Let her sleep to-night, or if she lay awake on this
vigil let her thoughts be serene as the summer night and radiant as
the summer dawn. He would beg a night's lodging from the Vicar, who
was already so deep in Sir Richard's bad graces that one more act of
defiance could not add to his offense.</p>
<p class="calibre3">Edward found his friend the Vicar, an old Tractarian who had somehow
eluded Sir Richard's Protestant zeal and been presented to the living
of Barton Flowers, sympathetic and encouraging. The old man sat in his
dusty room amid a chaos of theological tomes and held forth upon the
sacramental wonder of marriage, reaching from time to time for a book
from his shelves, usually the work of some Anglo-Catholic divine of the
seventeenth century, in whose sonorous periods human love was exalted
and sanctified and whose dying cadences<span id="pgepubid00022" title="[Pg 27]"><a id="Page_27" title="[Pg 27]"></a></span> showed forth mortality in the
image of Almighty God.</p>
<p class="calibre3">"Marriage is too sacred a rite to be regulated by worldly
considerations," the priest said. "You are justified by your singleness
of purpose. You have acted loyally to the woman of your choice. You
have nothing to reproach yourself for."</p>
<p class="calibre3">Edward had been glad to avail himself of the Vicar's help to assure
Elizabeth that she was not outraging decency by marrying him; but he
had never occupied his mind with the demands of religion, and only now
for the first time he was deeply impressed by a sudden consciousness
of what a weight of moral and spiritual support stood behind him in
what he was about to do. From that moment he looked at religion with
new eyes, apprehending in it the possibility of so crystallizing his
indeterminate aspirations as even at this late hour of youth to do
something and be somebody. He went up to bed in a glow of ambition that
lighted his spirit, even as the candle lighted the dark corridors and
stairways of the Vicarage.</p>
<p class="calibre3">Edward slept tranquilly, and in the morning at eight o'clock he was
married to Elizabeth Taylor, with nobody except her grandfather and a
couple of farm hands to hear their whispers of eternal fidelity, their
murmured promises to have and to hold and to cherish until death. There
was no shouting when they came out arm-in-arm from the church; there
was nothing except the peace of a mid-<span id="pgepubid00023" title="[Pg 28]"><a id="Page_28" title="[Pg 28]"></a></span>summer morning, the fragrance of
long grass in the churchyard, the hum of bees in the limes, and in the
distance a sound of lowing cattle.</p>
<p class="calibre3">The bride and bridegroom had not planned to spend their honeymoon
elsewhere; indeed, they had both been so much preoccupied with the
complications arising out of their simple action that they had thought
of nothing beyond the achievement of the wedding. When old James Taylor
asked them where they intended to pass the night, neither of them could
reply for the moment. At length Edward spoke:</p>
<p class="calibre3">"I have to explain, Mr. Taylor, that yesterday I had a very unpleasant
scene with Sir Richard, who ordered me out of his house forever."</p>
<p class="calibre3">"A' look now, that's Sir Richard sure enough," the old man nodded. "The
most unreasonable man that ever owned an acre. Well, I suppose you'd
better bide here."</p>
<p class="calibre3">Edward explained his project to stay on and help with the farm work, at
which the old man chuckled.</p>
<p class="calibre3">"You can stay so long as you will, but I don't reckon you'll be much
use on the farm. What's Lizzie say to it?"</p>
<p class="calibre3">Elizabeth had no words to say, but worlds to look, and since all her
worlds were entirely populated by Edwards, she showed plainly that she
approved of anything Edward proposed to do.</p>
<p class="calibre3">"'Tis no use at all to look for help from a maid, once she be tied up,"
the old man chuckled. "I sup<span id="pgepubid00024" title="[Pg 29]"><a id="Page_29" title="[Pg 29]"></a></span>pose I might soberly consider myself a
fool to give her to you. But give her I have. You see, Mr. Edward, you
was all her fancy, and ever since my boy died, her fancy has always
been mine. He was a good lad. I miss him sorely now, especially come
seed-time. And couldn't he broadcast a field of oats! Oh dear, oh dear,
none like him! Foxtail oats was his favorites, and wouldn't they come
up thick from his sowing! But, darn'ee, do you think the young chaps
can sow like that now? They cannot!"</p>
<p class="calibre3">"I'm going to have a good try," Edward vowed.</p>
<p class="calibre3">"A' look now, that's the way to talk, I'm bothered if it isn't," the
old man exclaimed, pretending to be much impressed, while his blue eyes
twinkled like the sea on a fine morning.</p>
<p class="calibre3">They had reached the farmhouse by now, and when old James had hung
his big beaver hat on a peg they sat down to the wedding breakfast,
at which the presence of the Vicar compelled a demeanor that might
otherwise have been wanting, because old James on such occasions was
apt to indulge in bucolic freedom of speech.</p>
<p class="calibre3">"But when parson's there," he said afterwards, "I always sits so dumb
as a rook in a pie. It comes over me to say summat, and then I catches
parson's eye and back the wicked words go into my mouth like rabbits.
He's a good man is parson, but he surely lays on me like snow in a
ditch. Well, now, go out into the fields and lanes and enjoy this fine<span id="pgepubid00025" title="[Pg 30]"><a id="Page_30" title="[Pg 30]"></a></span>
summer-time, you two. We can talk about Sir Richard and such sober
topics to-morrow. Come, give I a kiss, my maid. You're looking sweet as
laylock in a garden."</p>
<p class="calibre3">Edward would not let Elizabeth torment herself about the future, and
she, so deep in love, could not fret for long with Edward hers now
whatever happened ... hers ... ah, could he but know how completely
hers....</p>
<p class="calibre3">"Edward, my own, are you sure you love me as much, now that we are
husband and wife?"</p>
<p class="calibre3">She clung to him in a self-inflicted agony of doubt, knowing full
well that in a moment it would be turned to the warmth of a delicious
security.</p>
<p class="calibre3">"My foolish Lizzie," he murmured, "I love you a thousand times more."</p>
<p class="calibre3">"But you seem sad sometimes, as if you half regretted what you had
done."</p>
<p class="calibre3">"I am not sad, my dearest. If I seem serious, it is because I am awed
by the knowledge that you are mine."</p>
<p class="calibre3">"Oh, and I am yours, I am indeed yours."</p>
<p class="calibre3">Edward looked into her burning brown eyes that were unlike any other
brown eyes he had ever seen, because they caught somehow the hues and
shadows of her deep auburn hair, as a woodland pool appears stained
with autumn like the trees above.</p>
<p class="calibre3">"Your eyes," he murmured, and he felt a longing to drown within their
deeps.</p>
<p class="calibre3">"Do you like my eyes?"</p>
<p class="calibre3"><span id="pgepubid00026" title="[Pg 31]"><a id="Page_31" title="[Pg 31]"></a></span></p>
<p class="calibre3">"Elizabeth! You vain, vain Elizabeth!"</p>
<p class="calibre3">They kissed, while a summer wind sang its small song, its intimate and
idle song among the grasses at their feet.</p>
<p class="calibre3">"Hark to the little wind," said Edward. "Of what does it remind you?"</p>
<p class="calibre3">"Only of summer," she whispered.</p>
<p class="calibre3">"Don't you think it is like a child singing to herself while she plays
alone with her toys?"</p>
<p class="calibre3">"What funny fancies you have, Edward."</p>
<p class="calibre3">A sudden comprehension of what might be seized him in a rapture, and he
clasped his Elizabeth closer.</p>
<p class="calibre3">"Can you not tell me of what I am thinking?" he whispered.</p>
<p class="calibre3">She raised her eyes slowly, divined the thought that was beating from
his heart into hers, blushed as red as her own red heart which now beat
as fast as his, buried her face for a moment in his breast, then looked
up quickly and for answer gave him her lips.</p>
<p class="calibre3">The moths were dancing over the petunias, when the lovers came home to
the farm.</p>
<p class="calibre3">"But you'll never have such another day," said old James. "And if
you've missed your tea, you can make up for it with supper. But as for
me, I'm going to bed."</p>
<p class="calibre3">He lighted his candle and stumped upstairs, chuckling to himself. On
the landing he paused and leaning over the balustrade called down:</p>
<p class="calibre3"><span id="pgepubid00027" title="[Pg 32]"><a id="Page_32" title="[Pg 32]"></a></span></p>
<p class="calibre3">"And if you miss your breakfast, you can make up for it with dinner."</p>
<p class="calibre3">They could hear him still chuckling to himself long after he had closed
his bedroom door.</p>
<p class="calibre3">"You know," said Elizabeth, "if anybody didn't know it was grandfather
laughing to himself, they'd surely think it was owls in the roof."</p>
<p class="calibre3">They sat for a while talking about foolish things like that, and then
they too went upstairs. Through the open lattices of their room the
perfume of the night-scented stocks came up from the garden. Edward saw
with amazement that Elizabeth's hair reached almost to her feet, and
he thought of his mother's remark yesterday. "That little red-haired
girl!" Why, there never was such hair before. Too soon the moment came
to put out the candle and lose those glinting locks. While the odor of
the wick slowly faded upon the cool fragrance blown in upon them by the
night, Edward lay with the last vision of Elizabeth upon his inner eye.
Then turning he clasped her in his arms, and she with all her being
leapt to his as a wave to the shore.</p>
<p class="calibre3">There was no moon that night, but in every lattice a star or two
twinkled, and in the starshine Elizabeth lay beside him like a warm
shadow.</p>
<p class="calibre3">"Are you happy, my darling?"</p>
<p class="calibre3">"Very, very happy."</p>
<p class="calibre3">Edward could not sleep. He did not want to sleep. He wished to stay
forever like this, with her hair about his face. Dawn was on the
panes,<span id="pgepubid00028" title="[Pg 33]"><a id="Page_33" title="[Pg 33]"></a></span> and the sparrows were stirring in the eaves. How still she
lay, how fast she slept! He bent over to kiss her eyelids. She stirred
slightly and put out her hand, clasping his and murmuring a faint
endearment, an echo from her dreams. The first rays of the sun shone
through upon the bed. Her lips in sleep were very crimson, and she woke
up when he kissed them.</p>
<p class="calibre3">"Are you happy, my darling?"</p>
<p class="calibre3">"Very, very happy."</p>
<p class="calibre3">They lay for a long while in each other's arms while the sun climbed
higher, the bland five o'clock sun of June.</p>
<p class="calibre3">"I must start farming to-day," Edward declared. "They'll be cutting the
hay, I fancy."</p>
<p class="calibre3">"While the sun shines," she whispered, smiling.</p>
<p class="calibre3">"My lovely one, my lovely one," he breathed.</p>
<p class="calibre3">Edward was not given much time to test his willingness or ability as a
farmer, because as soon as the hay had been carried old James Taylor
was given notice by Sir Richard Flower to quit the farm at Ladyday. He
went up to the Hall to try to see his father and find out if the notice
would be rescinded should he himself give an undertaking to leave
the neighborhood. Sir Richard, however, declined to see his son, and
that evening he sent him his allowance for the next quarter with the
intimation that this was the last money he would ever receive from his
father. When the rent of his chambers and<span id="pgepubid00029" title="[Pg 34]"><a id="Page_34" title="[Pg 34]"></a></span> some outstanding debts in
London had been paid and his few possessions sold, Edward found himself
with not much more than one hundred and fifty pounds and without any
prospect of earning a living. It had never occurred to him that his
father would take what he thought so mean a revenge on his wife's
grandfather, and he could not help feeling that at the back of Sir
Richard's action was a desire to get rid of the old man who, as Edward
knew, he considered an unprofitable tenant. James accepted his notice
with admirable calm and dignity. He had not a word of reproach for
Edward or Elizabeth, and upon his landlord's behavior his only comment
was:</p>
<p class="calibre3">"It was always in my mind that he would give me notice one day. Ever
since I argued the point with him over that larch plantation which I
said was spoiling good grazing he had it in his heart to get rid of I.
He were ashamed for a long time. And I held the farm for thirty years,
and my father before me twenty-five years, and his father before that
thirty-two years."</p>
<p class="calibre3">Edward was in despair; but neither old James nor Elizabeth would hear
of his reproaching himself.</p>
<p class="calibre3">"You came like an honest man," said James, "not gallivanting round as
one of the gentry might. That's good enough for I, and that's good
enough for she. Come Lammas I shall be seventy, which is a late age for
making a long journey unless it be that powerful long journey out of
this world into<span id="pgepubid00030" title="[Pg 35]"><a id="Page_35" title="[Pg 35]"></a></span> the next. My brother Henry set out forty-three years
ago for Australia, and I've only heard from him once, and that was
Christmas two years ago. I put off answering the letter, for I write
a very crooked hand and was never one for letters. Get the ink-horn,
Lizzie. By God A'mighty, I'll write him now and say we'll pay him a
visit in the spring of the year. I still have a pretty hand wi' sheep,
and I reckon Henry will find us all a job."</p>
<p class="calibre3">"Why, that's the very thing," cried Edward. "Emigration! What a fool I
was not to have thought of it myself."</p>
<p class="calibre3">Old James wrote a letter to his brother, who was evidently a squatter
of considerable affluence and who, judging by the cordial tone of
the only letter he had written, would be glad in his old age, being
a childless widower, to welcome his kinsfolk from England. It was
decided to give up the farm at Christmas and to sail as early as
possible in the New Year. Fortune was kind up to the point of granting
old James a good harvest that season, and although the new tenant,
Farmer Wilberforce, did not pay as he ought to have paid for the live
and dead stock, James scraped together enough to enable the three of
them to equip themselves for a long journey and avoid the steerage. It
became important to start as early as possible in the New Year, because
Elizabeth was with child, and it was hoped to leave England in time
to avoid childbirth on the high seas. Edward was painfully conscious
of being<span id="pgepubid00031" title="[Pg 36]"><a id="Page_36" title="[Pg 36]"></a></span> able to do very little to help old James, and he tried to
console himself with the belief that once ashore in Australia he would
by his energy make amends for his present helplessness. Places were
secured in the second class of the <i class="calibre8">Mariana</i>, a ship of 1,374 tons,
which was due to sail from Liverpool in the first week of January; but
on the night before her departure, when all on board were making merry
to the strains of an emigrant fiddler and an emigrant piper, an alarm
of fire was raised. There was plenty of help at hand from the crowded
shipping of the Mersey; the passengers with their luggage were taken
off by steam-tugs and boats; and the vessel was run ashore, where when
the tide left her high and dry she was gutted by the flames.</p>
<p class="calibre3">Elizabeth bore the terrifying experience with fortitude, and when the
shipping agents in Liverpool told them of a ship sailing from London
within a few days she was not backward in urging her husband and
grandfather to make every effort to obtain a passage. Nevertheless, the
nervous tension to which she had been exposed proved too much for her,
and even before the train journey was accomplished her travail began
prematurely. There was no time to search for comfortable lodgings.
The first rooms they found in Pomona Terrace, a dreary by-street off
the Euston Road, had to serve their need, and while the landlady, a
good-natured, grubby Irishwoman, helped Elizabeth to bed, Edward<span id="pgepubid00032" title="[Pg 37]"><a id="Page_37" title="[Pg 37]"></a></span>
rushed out into the wet foggy night to summon a doctor.</p>
<p class="calibre3">In a crescent of decaying houses he soon perceived like rubies on the
murky air the lamps of two doctors adjacent. Had his need been less
urgent, such a juxtaposition would have presented an insoluble problem
to Edward whose attitude to life was one long hesitation between his
right and his left. As it was, he hurried up the first pair of steps
and was on the point of pulling the bell, when through a broken slat
of the Venetian blinds he saw the occupant of the room thus revealed
pour himself out a very generous allowance of whisky or brandy from
a dusty decanter unsteadily held in a dirty hand. He paused with his
fingers on the knob of the bell while the occupant of the gas-lit room
held the decanter in mid-air, listening. The face with its expression
of interrupted desire and expectant cunning was so repulsive that
Edward was horrified at the idea of such a creature's attending upon
his beloved Elizabeth, and in a moment he had put his leg over the low
stone parapet that divided the steps of one doctor from the steps of
the other.</p>
<p class="calibre3">"Yes," the maid told him, "Dr. Harrison is in."</p>
<p class="calibre3">Edward explained the case to the doctor, while the latter packed his
bag with various instruments, and so much excited was he that he could
hardly refrain from plucking at the doctor's sleeve when they were
hurrying back through the fog to the lodgings in Pomona Terrace. Dr.
Harrison was a young<span id="pgepubid00033" title="[Pg 38]"><a id="Page_38" title="[Pg 38]"></a></span> man, scarcely more than thirty, whose manner
carried such assurance that Edward was able to feel that his choice
had been the right one. When they reached the house the doctor was at
once taken upstairs, while Edward, on the suggestion of Mrs. Gallagher,
accommodated himself in the kitchen. Here he found old James Taylor
on one side of the range talking of agriculture to Mr. Gallagher,
a workman in the employ of one of the great railway companies, who
was displaying his agreement or disagreement with, his interest in
or boredom at the farmer's observations only by the way he sucked at
his clay pipe. For an Irishman he was strangely taciturn. From time
to time slatternly young women passed through; but whether they were
the daughters or servants of the Gallaghers or lodgers in the house,
there was no telling. Though Edward took a chair next old Taylor, his
thoughts were upstairs, and the old man's lecture on clovers meant
as little to him as the chirping of the crickets in the walls of the
house. His anxiety over Elizabeth was so sharp that the incongruity
of his surroundings never struck him. His whole being was too much
involved with his wife's for what is called reality to affect him more
nearly than might the incidents of a dream. That he should share a
bedroom with old Taylor, that he should find himself deferring to a
Gallagher, that he should be expected to take his place at an ill-laid
table and eat the malodorous mess there set down by a slut with grimy
hands made<span title="[Pg 39]"><a id="Page_39" title="[Pg 39]"></a></span> no impression upon Edward. Had his mother floated into
this squalid kitchen and pointed a delicate finger in scorn at his
surroundings, he would not have listened one whit less intensely for
the slightest sound from above. Whatever disgusted him in Pomona
Terrace took its place in the general purgatory of deprivation of the
sight of Elizabeth, and the farthest that his mind wandered from that
room upstairs was to that broken slat in the Venetian blinds through
which he had seen that drunken doctor's face. Thank God he had! Thank
God!</p>
<p class="calibre3">A long time passed. Mr. Gallagher had fallen asleep and was snoring
loudly. Old Taylor was asleep too, his jaw dropped upon his chest, his
whole aspect senility incarnate. The restless slatterns no longer moved
in and out with unwashed dishes and bawdy gigglings.</p>
<p class="calibre3">Now the crickets were silent in the glooms; Gallagher had ceased to
snore; there was no sound except an occasional cough from the subsiding
fire. So silent was it that Edward could hear his watch ticking with
what seemed a terrible rapidity in the pocket of his waistcoat. At last
the doctor put his head round the door and beckoned. Edward was beside
him in a moment, gasping forth his alarms.</p>
<p class="calibre3">"She's not dead? Why didn't you call me sooner?"</p>
<p class="calibre3">The doctor gripped Edward's forearm and bade him pull himself together.
That grip in conjunction with the cold air of the passage kept Edward<span id="pgepubid00034" title="[Pg 40]"><a id="Page_40" title="[Pg 40]"></a></span>
from breaking down. He compelled himself to follow what the doctor
with a good deal of technical detail was telling him about Elizabeth's
condition. "And so it may be impossible to save the lives of both," the
doctor was murmuring. "However, as I was saying, there is no need to
decide yet, and I am expecting a new instrument to-morrow, which may do
away with the necessity of such a painful decision. I have sent word to
have any parcel which arrives for me brought here immediately."</p>
<p class="calibre3">"Decision?" Edward repeated. "What decision do you mean?"</p>
<p class="calibre3">"Whether we save the life of the mother or the child."</p>
<p class="calibre3">"Are you drunk or mad?" Edward shouted. "Why, rather than she should
suffer an instant's pain I would have the child cut to pieces.
Decision! I believe you're as drunk as that other fellow next door."</p>
<p class="calibre3">The young doctor gazed at Edward in astonishment, for he had heard
nothing of the reason for choosing him in preference to his neighbor.</p>
<p class="calibre3">"I was bound to give you the opportunity of deciding," he explained.
"Naturally I did not for a moment expect you to give any other answer
but the one you have given. I'm sorry to have upset you like this. If
I may offer you some sound advice, I should recommend your staying
quietly in the kitchen. It would be better, of course, if you could
manage to lie down and sleep for a while;<span id="pgepubid00035" title="[Pg 41]"><a id="Page_41" title="[Pg 41]"></a></span> but I can understand that
you are too anxious for that. You must not work yourself up into a
state of mind. There is no immediate cause for anxiety. No, certainly
no immediate cause."</p>
<p class="calibre3">Edward allowed the doctor to steer him back into the kitchen where
their entrance roused old James Taylor and Mr. Gallagher, both of whom
with loud yawns declared their intention of going to bed. Soon Edward
was left alone, for Dr. Harrison went back to his patient, and he
settled himself down to solitary meditation by what was left of the
dead fire in the still fairly warm grate.</p>
<p class="calibre3">It was easy, thought Edward, very easy for that young doctor to talk
about childbirth as if it were nothing more than buying a doll in a
shop. Doctors soon began to lose their sense of the soul in their
familiarity with the body. What did a man like that know about the
great mystery of human love? Edward's mind went back to his talk with
the Vicar of Barton Flowers on the vigil of his wedding; and now
thinking over his brief married life with Elizabeth he apprehended
all the truth of what the parson had said. He remembered how much the
conversation had elated him at the time and how he had felt an impulse
to submit himself to the promptings of what the Vicar had called the
Grace of the Holy Spirit. That impulse like so many of them, alas,
had gone the way of the rest, had been allowed to expire when the
enthusiasm of thought demanded the breath of action to endure. Edward
vowed that<span id="pgepubid00036" title="[Pg 42]"><a id="Page_42" title="[Pg 42]"></a></span> this time if Elizabeth's life should be granted to him he
really would ... what? "I really will grapple with life," he promised
to that nebulous emanation of celestial magic which the ordinary man
calls God. "Before the sun rises to-morrow morning I may be a father.
I shall owe a duty to a human soul which I have brought into the
world." Edward discovered with shame that this child, which might
even at this moment be uttering its first cry to the darkness of the
unimaginable universe around it, had not until this moment presented
itself to him as a fact. He regretted now the way he had answered the
doctor's question a short while ago. He had been sneering to himself
at the doctor's point of view about childbirth; but he should rather
have sneered at himself for what he was, a weak and self-indulgent and
careless egoist that without foresight and without responsibility might
become the parent of a human being from whom in days to come he should
expect gratitude, affection, and obedience.</p>
<p class="calibre3">Edward made new vows to that dim God beyond the stars that if he were
granted not only the life of his Elizabeth, but also the life of their
child, he would devote his future to a worthy fatherhood, that even
if himself should fail in his contest with life he would ... what?
Edward's mind wandered already to the agony of his adored wife, and he
could not bear to contemplate any future at all until he knew that she
was safe.</p>
<p class="calibre3"><span id="pgepubid00037" title="[Pg 43]"><a id="Page_43" title="[Pg 43]"></a></span></p>
<p class="calibre3">Safe! The word wrote itself in the cracks of the dingy ceiling, in the
pattern of the grimy wall-paper, in the ashes of the dead fire, in the
scrolls of iron-work upon the range, in the patchwork hearthrug, in the
knots and lines of the kitchen table. Safe! The word acquired such a
force and power of its own that Edward almost worshiped it by repeating
aloud the mere sound of it in apostrophe after apostrophe of awful
contemplation. Safe! He clutched at the S as he would have clutched at
a rope to drag himself up from the abyss below. He climbed up the A,
up the F, up the E to stand on the high ground above with Elizabeth
clasped in his arms ... with her ... with his darling ... safe....</p>
<p class="calibre3">Edward fell asleep. At four o'clock Mrs. Gallagher came down to tell
him that things were going on as well as could be expected upstairs,
recommending him to be off and lie down on his bed like a Christian.
The only place where Edward wanted to lie down was on the landing
outside the door of Elizabeth's room; but there was still enough left
of the younger son of Sir Richard Flower to keep him from making such a
proposal to the landlady.</p>
<p class="calibre3">It was like Edward to insist on staying in the kitchen. There was about
it the kind of ineffective Quixotry to which he had been addicted all
his life. At seven Dr. Harrison came in and made him comparatively
happy by asking him to go round to his house, wait for the postal
delivery, and bring back<span id="pgepubid00038" title="[Pg 44]"><a id="Page_44" title="[Pg 44]"></a></span> any package that might by good luck be left.
The doctor wanted to add that Edward would do well to ask his servant
to give him some breakfast, there being little likelihood of his eating
any breakfast in Pomona Terrace. On second thoughts, he did not think
the suggestion worth making. He felt too much fatigued by his own
all-night vigil to argue with an unbalanced fool like this husband.
Such was the young doctor's mood on this drear January morning. Edward
hurried through the wet twilight, pale, unshaven, his hair and whiskers
unkempt, his collar dirty, his clothes frowsy from the kitchen where
he had spent the night. At such an hour Manning Crescent looked more
dilapidated than on the night before, and the broken slat in the
Venetian blinds of the drunken doctor next door gave the last touch of
raffish squalor to the row of houses.</p>
<p class="calibre3">No sooner was Edward seated in the doctor's neat consulting-room than
he fell into a despair, because he had not seen Elizabeth before he
came out. Suppose she were dead when he arrived back? He could hardly
stay in the room; the smell of disinfectants here was stifling him
with an aroma of death. It was only by clutching the arms of the chair
covered with red rep and dinning into his brain the need to wait for
that blessed instrument which was so anxiously awaited by the doctor
that he was able not to desert his post. He rang for the maid to ask
when the parcel delivery might be ex<span id="pgepubid00039" title="[Pg 45]"><a id="Page_45" title="[Pg 45]"></a></span>pected, and upon her telling him
"ten o'clock at the earliest" he groaned aloud.</p>
<p class="calibre3">It was actually eleven o'clock when Edward, after spending hours of
uncertainty, ran back to Pomona Terrace with a parcel for Dr. Harrison.</p>
<p class="calibre3">"It has arrived at the very moment I required it," said the doctor,
hurrying upstairs without waiting to give Edward a word of hope about
his wife's condition beyond his satisfied comment on the arrival of the
new instrument.</p>
<p class="calibre3">On the husband left standing in the dim passage of the Gallaghers'
lodging-house dawned the apprehension of God's mercy to him in
directing his footsteps yesterday evening to where lived probably the
only doctor in the neighborhood who would have a chance of saving that
beloved woman's life. He fell upon his knees where he stood and prayed
that God's mercy should be extended to the fulfillment of all he hoped.
Then he went back to the kitchen. At last Mrs. Gallagher entered all
smiles.</p>
<p class="calibre3">"You may come up now," she told him. "All's well, praise be to the Holy
Mother of God, and you've a girleen."</p>
<p class="calibre3">"A what?" shouted old James Taylor from his chair by the fire.</p>
<p class="calibre3">"'Tis you that's a great-grandfather, Mr. Taylor," she told the old man.</p>
<p class="calibre3">"A' look now," chuckled old James. "They ought to put I in a show. 'Tis
a state of life you can see in rams and bulls when you've a mind, but<span id="pgepubid00040" title="[Pg 46]"><a id="Page_46" title="[Pg 46]"></a></span>
darn'ee, a real great-grandfather is summat to stare at even in London."</p>
<p class="calibre3">Edward was kneeling beside Elizabeth before the old man had finished
his sentence. The doctor left them together.</p>
<p class="calibre3">"You haven't asked to look at baby," Elizabeth murmured reproachfully.
She lifted the bedclothes for a moment, while Edward looked.</p>
<p class="calibre3">"Yours," he murmured.</p>
<p class="calibre3">"Ours," she corrected. "Ours, my dearest."</p>
<p class="calibre3">"But it was you that suffered everything!"</p>
<p class="calibre3">"But you were anxious about me, weren't you, my darling?" she tenderly
asked.</p>
<p class="calibre3">"Elizabeth! Elizabeth!" he cried.</p>
<p class="calibre3">She knew how much he too had suffered, and because he did not speak of
his emotions she was moved with an immense desire not to let her baby
make her careless of her husband. She put out her hand to stroke his
head.</p>
<p class="calibre3">"I thought about you all the time," she whispered. "When the pain was
worst I kept saying, 'Edward! Edward!' Nothing else. Only that, my
darling."</p>
<p class="calibre3">He drew her hands to his lips.</p>
<p class="calibre3">"And what shall she be christened?" the mother asked. "I'd like her to
be called Mary," she went on breathlessly. "You wouldn't think that a
common name, would you? 'Mary Flower,' I think that's a pretty name."</p>
<p class="calibre3">The doctor came in at this moment to suggest that Edward had stayed
long enough for the pres<span id="pgepubid00041" title="[Pg 47]"><a id="Page_47" title="[Pg 47]"></a></span>ent. The father leant over to kiss the eyes of
that pale mother.</p>
<p class="calibre3">"Well, have 'ee seen her?" demanded the great-grandfather when Edward
came downstairs. "I suppose it is a girl all right?"</p>
<p class="calibre3">"Mary," said Edward.</p>
<p class="calibre3">"Oh, Mary, is it to be? Well, that's a good down-right honest style of
name for a girl. Yes, 'tis a name I like very well. And don't you be
discouraged, Mr. Edward, if she don't look beautiful all at once. A
calf's different. A new-born calf is a pretty sight for any man. So's a
chicken fresh from the egg. But a baby is so ugly as a young rook. Oh
dear, oh dear, there's few more ugly sights for man to look upon than a
new-born baby. Yet I suppose this time come three months we shall all
be looking at kangaroos and wobblies, and they're worse by what one
reads of 'em."</p>
<p class="calibre3">Old James had got hold of one or two volumes of Antipodean travel, from
dipping into which behind a pair of large horn spectacles he had formed
a picture of Australia as zoölogically rich as a medieval illuminator's
conception of Eden.</p>
<p class="calibre3">Now that Elizabeth's child was safely born, it was imperative to leave
England before the money gave out, and berths were secured in the
<i class="calibre8">Wizard Queen</i>, a 1,000-ton steamer lying in the East India Docks,
which was due to sail on February 18th for the port of Sydney. They
went on board the preceding afternoon; but remembering their last
experience in the<span id="pgepubid00042" title="[Pg 48]"><a id="Page_48" title="[Pg 48]"></a></span> <i class="calibre8">Mariana</i> and with a desire to avert ill-luck they
kept themselves out of sight of the other emigrants who, like those on
the Mersey, were drowning the sorrow of departure in music and song. As
a matter of fact, old James could not resist going up on deck to have a
bit of fun, as he called it; but Edward and Elizabeth remained close in
their cabin, and did not even emerge for supper.</p>
<p class="calibre3">After supper the strains of music that floated down below became more
melancholy, so that Elizabeth shivered. Edward, always solicitous,
begged her to tell him what was the matter. Did she feel nervous after
that alarming night on the Mersey? Did she regret that she had married
him? Was not the baby Mary sleeping soundly in her canvas cot, and was
she not so wonderful a being that sighs should be forgotten in the
contemplation of the future they two would sacrifice all to achieve for
her?</p>
<p class="calibre3">"I was thinking of you, Edward," she said. "I was wondering if I had
done wrong in marrying you, so that you have had to leave your family
and your country for my sake."</p>
<p class="calibre3">"It is not your fault," Edward exclaimed, "that we are all going to
Australia. If my father had not shown himself so bitter against me,
if my mother had not lost all affection for me, we should still be in
Long Orchard Farm. I beg you, my dearest, not to distress yourself with
regrets. Besides, look at her." He pointed to the sleeping infant.
"What<span id="pgepubid00043" title="[Pg 49]"><a id="Page_49" title="[Pg 49]"></a></span> do we matter now? We have given <i class="calibre8">her</i> to the world. There never
was such a baby girl. Just think, my Elizabeth, what you endured, what
even I endured? And now look at her. Remember how fortune was on her
side. I was never a religious man, but since that night in January I
have been made conscious of a divine power that directs the universe.
In the miracle of that small but perfect being born to you and me I
have understood the great miracle of creation."</p>
<p class="calibre3">Elizabeth regarded her husband with admiration and awe.</p>
<p class="calibre3">"You have such noble thoughts," she flattered him. "But I cannot think
like you. I can only remember the meadows where I played as a child
and the clematis over the door and the cocks crowing on fine mornings
in the summer-time. And now I'm leaving all that. And grandfather is
leaving it. And you, my Edward, are being taken far over the sea to
another country, and all because I selfishly loved you and selfishly
let you love me."</p>
<p class="calibre3">The notes of a melody more sad than any which had yet been played
drifted below from the deck. Elizabeth's tears fell fast like raindrops
upon the petals of a rose. Nor could Edward's most tender words console
her grief, nor could they mitigate her apprehensiveness nor lighten the
gloom in which for her the future was enveloped. It was not until her
grandfather came tapping at their cabin door, in which a moment later
he stood framed with his<span id="pgepubid00044" title="[Pg 50]"><a id="Page_50" title="[Pg 50]"></a></span> ruddy and jocund countenance, that she smiled.</p>
<p class="calibre3">"I've enjoyed myself rarely," he announced. "There's been singing
up there as would put the heart in any man. There was never better
fiddling in Barton Flowers, not when I was a nipper. I'm bothered if I
han't enjoyed myself. Sleep like a top, the saying is. Bothered if I
shan't sleep like a humming-top to-night."</p>
<p class="calibre3">He wished them well and stumped off to his berth.</p>
<p class="calibre3">"You see how happy the old man is!" Edward exclaimed.</p>
<p class="calibre3">And Elizabeth, now that the music had ceased to play upon her emotion,
forgot her fears. In the morning when they went on deck London was
sailing past them on either side of the ship, and a sharp wind from the
northwest made the voyagers long for warm and sunny lands.</p>
<p class="calibre3">Among the emigrants was a middle-aged couple called Fawcus, both of
whom showed themselves most friendly to the Flowers, and both of whom
much admired the good behavior of the baby girl. Mr. Fawcus was a
large, smooth-faced man of fifty, definitely parsonic in the general
impression he gave with his suit of black broadcloth, a primness of
manner that was noticeable in so large a man, and an inclination to
discourse in rotund sentences. His wife was in every way a contrast to
her husband, being small, restless, and quick-eyed, a woman obviously
belonging to an inferior class, but who in spite<span id="pgepubid00045" title="[Pg 51]"><a id="Page_51" title="[Pg 51]"></a></span> of her Cockney accent
and vulgarity was obviously the leader and looked after her husband as
sharply as a capable and well-trained nurse.</p>
<p class="calibre3">"I hope Mr. Micawber will still be alive when we reach Australia," said
Edward. "Nobody else could keep pace with our friend."</p>
<p class="calibre3">"You haven't spoken of Mr. Micawber before, dear," said Elizabeth
reproachfully. "I didn't know you had any friends in Australia."</p>
<p class="calibre3">"Mr. Micawber is a character in one of Mr. Charles Dickens' novels," he
explained.</p>
<p class="calibre3">"Mr. Dickens who wrote the <i class="calibre8">Christmas Carol</i>?" Elizabeth asked.</p>
<p class="calibre3">"The same."</p>
<p class="calibre3">"He must be a very nice gentleman," was her murmured comment.</p>
<p class="calibre3">Edward laughed.</p>
<p class="calibre3">Mr. Fawcus according to himself had been reduced to emigration by the
devotion of his energy, his talents, and his money to the great cause
of popular education.</p>
<p class="calibre3">"My survey of history," he told Edward, "taught me that all the
greatest human beings have been teachers. I determined, however humbly,
to follow in their footsteps."</p>
<p class="calibre3">According to Mrs. Fawcus her husband's mistake had been first in
wasting money on derelict schools, and secondly in destroying whatever
chance such schools had of recovery by preaching in the open air of the
locality on Sundays.</p>
<p class="calibre3"><span id="pgepubid00046" title="[Pg 52]"><a id="Page_52" title="[Pg 52]"></a></span></p>
<p class="calibre3">"People couldn't a-bear to send their children to Mr. Fawcus's school
when they saw him preaching in the market-place like any heathen
missionary. It gave them the idea he was funny, and so the scholars'ud
leave until there wasn't one left, and then Mr. Fawcus had to move to
another town and start over again with another school. Besides, I was
always a hindrance to him."</p>
<p class="calibre3">"You were never a hindrance, my love."</p>
<p class="calibre3">"Oh, yes, I was, Mr. Fawcus, and well you know it. The truth is
parents don't want a homely woman like me for a teacher. They look for
something quite different in a school-mistress, something tall and
starchy."</p>
<p class="calibre3">"And what are you going to do in Australia?" Edward asked.</p>
<p class="calibre3">"In Australia, my dear sir," Mr. Fawcus boomed, "in Australia I am
going to educate the aborigines, who I understand from the reports of
travelers are considered the most degraded race of human beings on this
earth. Should that prove truer than the majority of travelers' tales
there must be room for education. After my experience with the children
of...."</p>
<p class="calibre3">"Hush!" his wife interjected. "Hush, Mr. Fawcus!"</p>
<p class="calibre3">"After my experience with the last school I founded in England the
aborigines of Australia will be easy to manage. Their women, I believe,
are known as ginns. A most unbecoming designation.<span id="pgepubid00047" title="[Pg 53]"><a id="Page_53" title="[Pg 53]"></a></span> I shall try to
persuade them to abolish that name. The sea is rising, I observe
with regret. We are liable to pass a rough night, I fear. And I am
usually right. In fact, my intimates often nickname me Mr. Forecast.
My own name, by the way, is remarkable, don't you think? I have been
tempted to speculate upon its origin, and I have sometimes fancied
that it might be found among the <i class="calibre8">senatus populusque Romanus</i>. I was
informed the other day, however, by a gentleman of curious etymological
knowledge that it is probably a local variant of Fawkes. You of course
remember Guy of that ilk? Yes, the sky is looking very dirty indeed."</p>
<p class="calibre3">A steely dusk of northwest weather lay chill upon the <i class="calibre8">Wizard Queen</i>
when she was tossing in the Downs, and by night the wind was blowing
with hurricane fury from the Kentish coast. The music and motion of the
storm kept all on board awake, and when about three o'clock there was
a crash followed by a dreadful sound of grinding timbers, the faces of
the terrified passengers immediately appeared from every cabin.</p>
<p class="calibre3">Edward bade his wife wrap up the baby while he found out what had
happened, and with only an overcoat over his nightshirt he forced his
way on deck. From the darkness on the port side a shape seemed to carve
itself to the fleeting likeness of another vessel, but it vanished so
quickly that Edward fancied the vision to be a chimera of the night.</p>
<p class="calibre3">"All hands to lower the boats," a voice cried<span id="pgepubid00048" title="[Pg 54]"><a id="Page_54" title="[Pg 54]"></a></span> from forward in the murk
of the night. Figures in dripping oilskins, like monsters risen from
the sea, pushed Edward aside to get at their business; but he managed
to make his way back aft to where from the orange mist above the
saloon-companion a stream of disheveled passengers belched forth like
smoke waving in the blast. As he fought his way down to find Elizabeth
and the old man, he heard another shout for'ard.</p>
<p class="calibre3">"The port lifeboat was smashed in the collision."</p>
<p class="calibre3">The saloon was empty when Edward reached it, and he was on the point of
turning back to begin a distracted search on deck when Elizabeth came
out of her cabin carrying the baby, her hair all about her shoulders,
her aspect serene. She looked fragile, ethereal indeed, but amid all
that confusion of human terror into which she must shortly be plunged
she moved forward with the resolution of an angel. Behind her came the
old man wrapped in a plaid shawl above his nightshirt, his face ruddy
as ever, but his old legs appearing thin as twigs beneath, so that it
seemed as if he must be blown away into the night when he should face
the storm.</p>
<p class="calibre3">The crew was lowering the jolly-boat full of passengers when they
reached the deck. Notwithstanding the peril, for the ship would not
float another ten minutes it was being shouted, two of the sailors were
arguing angrily about the rig of the craft that struck the <i class="calibre8">Wizard
Queen</i>. One declared with an oath that it was a schooner; the other<span id="pgepubid00049" title="[Pg 55]"><a id="Page_55" title="[Pg 55]"></a></span>
affirmed with equal vigor that it had been a barquantine. A ship's
officer hurrying by fell to cursing them for rascals that they should
stand there arguing when the starboard lifeboat must be launched.</p>
<p class="calibre3">"Women and children first," the captain thundered.</p>
<p class="calibre3">A fiercer squall drummed overhead, and the emigrants that still
remained in the ship huddled together in fear of that dreadful brew
of waters upon which they were soon to float away. Somebody urged
Elizabeth toward the lifeboat; but she drew back.</p>
<p class="calibre3">"Let somebody go instead of me. I'll wait for my husband," she said.</p>
<p class="calibre3">But it happened that she was the last woman left and that there was
still room for Edward and old James, so that presently all three
climbed into the lifeboat.</p>
<p class="calibre3">"Lower away!"</p>
<p class="calibre3">The lifeboat rocked for a moment in the davits; and then just as she
reached the sea, being full in the weather, she was driven with great
force against the ship's side and stove in. She seemed to be brimming
over, but nevertheless the crew managed to shove her off, although by
this time she was so deep in the water that the wretched passengers
sitting on the thwarts were submerged from the waist downwards. It was
only the cork in her compartments that kept her barely afloat. There
were many faces still looking down from the steamer, and all on<span id="pgepubid00050" title="[Pg 56]"><a id="Page_56" title="[Pg 56]"></a></span> board
probably went down with her, or if the cutter was launched she must
have been swamped immediately, for a minute or two later the <i class="calibre8">Wizard
Queen</i> rose forward in the air and sank stern first. Now one by one,
as the icy waves broke over them, the women and men in the lifeboat
dropped from exhaustion into the sea. Old James Taylor was among
the first to go, falling backward without a cry, without a word of
reproach, as silently as one of his own red apples might fall at home
in the first October gale.</p>
<p class="calibre3">"Did the Captain say where we was?" asked the man at the tiller.</p>
<p class="calibre3">"Abreast of Beachy Head," one shouted in reply, and as he spoke a wave
swept him and a woman and a child into the darkness.</p>
<p class="calibre3">"O God! he's dead. He's lying dead in my arms!" cried a miserable
father who was holding in his arms a little boy of five or six.</p>
<p class="calibre3">"Drop the body overboard," the man at the tiller shouted. "Every pound
tells. Lighten the boat," he roared angrily. "Lighten the boat!"</p>
<p class="calibre3">The wretched father, clasping his dead child more closely, turned away
in indignation at the brutal order; but as he turned a wave swept him
and the body overboard, and the boat was lightened a little more.</p>
<p class="calibre3">All this time Elizabeth said nothing; but she clung to her place with
one hand and with the other held the baby to her breast beneath her
cloak. All this<span id="pgepubid00051" title="[Pg 57]"><a id="Page_57" title="[Pg 57]"></a></span> time Edward said nothing; but he had somehow managed
to wedge his legs round a support, so that whenever Elizabeth trembled
in her seat he could put out two arms to save her from peril. Two
women died of exhaustion from the wash of the sea and the freezing
wind, and their bodies were at once flung overboard. On the other
side of Elizabeth, Mrs. Fawcus, who alone of the women appeared to be
completely dressed, was trying to get off her cloak to throw it round
the mother, and simultaneously making an effort to listen to the plans
of Mr. Fawcus for the future, should the lifeboat ever reach a harbor.</p>
<p class="calibre3">"I have given up my plan of educating the aborigines of Australia," he
bellowed above the gale, making a megaphone with his hands.</p>
<p class="calibre3">"Yes, dear, I'm sure I agree with you. Drat this hook! It's so bent I
can't get it out of the eye. And don't put up your hands and shout at
me, Mr. Fawcus. You'll be swept over if you do."</p>
<p class="calibre3">"What has occurred once," Mr. Fawcus bellowed, "may easily occur again.
I shall inquire for a suitable post in London. I always did execrate
the sea, as you know, my dear."</p>
<p class="calibre3">Mrs. Fawcus nodded.</p>
<p class="calibre3">"And I was right," he shouted. "I usually am." But his wife could not
applaud his wisdom, for at that moment Elizabeth, reeling, cried:</p>
<p class="calibre3">"Take my baby. Edward! My darling, my<span id="pgepubid00052" title="[Pg 58]"><a id="Page_58" title="[Pg 58]"></a></span> darling, I ought not to have
married you. It's all my fault."</p>
<p class="calibre3">As Mrs. Fawcus took the baby, Elizabeth fell, and Edward, throwing
himself backward in a last effort to save his wife, fell with her into
the sea.</p>
<p class="calibre3">At dawn a small steamer sighted the wreck of the lifeboat and launched
a boat to rescue the four who had survived that night. One was the
man at the tiller; the other three were Mr. and Mrs. Fawcus with Mary
Flower.</p>
<hr class="chap"/>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0003.vue?5683ffb2